/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
"use client"

import type { VariantProps } from "class-variance-authority"
import type { ComponentProps, FC, ReactNode, SVGProps } from "react"
import React, {
	forwardRef,
	memo,
	useCallback,
	useMemo,
	useRef,
	useState,
} from "react"
import { AnimatePresence, motion } from "motion/react"
import { IconCircleWarningOutline18 as AlertCircleIcon } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import type { IconType } from "../../../icons"
import { Label } from "../label"
import { inputVariants } from "./input-variants"

export interface AutosuggestInputProps
	extends ComponentProps<"input">, VariantProps<typeof inputVariants> {
	label?: ReactNode
	name: string
	inputClassName?: string
	suffix?: ReactNode
	hint?: ReactNode
	errorMessage?: ReactNode
	showErrorMessage?: boolean
	icon?: LucideIcon | IconType
	iconClassName?: string
	iconPosition?: "left" | "right"
	iconOnClick?: () => void
	truncate?: boolean
	isRequired?: boolean
	postfix?: string
	allowPasswordManager?: boolean

	suggestions: string[]
	onSuggestionClick: (suggestion: string) => void
	customList?: ReactNode
}

export const AutosuggestInput = memo(
	forwardRef<HTMLInputElement, AutosuggestInputProps>(
		(
			{
				className,
				label,
				inputClassName,
				name,
				suffix,
				hint,
				errorMessage,
				showErrorMessage = true,
				type = "text",
				icon: Icon,
				readOnly,
				iconPosition = "left",
				iconClassName,
				iconOnClick,
				truncate,
				variant,
				textSize,
				postfix,
				allowPasswordManager = false,
				onSuggestionClick,
				suggestions = [],
				customList,
				...props
			},
			ref,
		) => {
			const [isFocus, setIsFocus] = useState(false)
			const [isHovered, setIsHovered] = useState(false)
			const suggestionListRef = useRef<HTMLDivElement>(null)

			const filteredSuggestions = useMemo(() => {
				const inputValue = props.value
				if (!inputValue || typeof inputValue !== "string") return []

				const lowercaseInput = inputValue.toLowerCase()
				return suggestions.filter((suggestion) =>
					suggestion.toLowerCase().includes(lowercaseInput),
				)
			}, [suggestions, props.value])

			const handleBlur = useCallback(
				(e: React.FocusEvent<HTMLInputElement>) => {
					// Check if the new focus target is within the suggestion list
					if (
						suggestionListRef.current &&
						e.relatedTarget &&
						suggestionListRef.current.contains(e.relatedTarget as Node)
					) {
						return // Don't lose focus if clicking on suggestions
					}

					// Use a small delay to allow for mouse events to complete
					setTimeout(() => {
						if (!isHovered) {
							setIsFocus(false)
						}
					}, 100)
				},
				[isHovered],
			)

			const handleSuggestionClick = useCallback(
				(suggestion: string) => {
					onSuggestionClick(suggestion)
				},
				[onSuggestionClick],
			)

			return (
				<div className={cn("flex w-full flex-col", className)}>
					{label && (
						<Label className="mb-1.5" htmlFor={name}>
							{label}
						</Label>
					)}
					<div className="flex flex-row gap-3">
						<div
							className={cn("inline-flex flex-1", {
								"shadow-sm": variant === "outline",
							})}
						>
							<div className="relative flex-1">
								{Icon && (
									<Icon
										onClick={(e) => {
											e.preventDefault()
											iconOnClick && iconOnClick()
										}}
										className={cn(
											"absolute top-1/2 h-4 w-4 -translate-y-1/2 cursor-default text-gray-400 transition-all",
											iconClassName,
											iconPosition === "left" ? "left-2" : "right-2",
											iconOnClick
												? "hover:text-gray-500 dark:hover:text-gray-300"
												: "pointer-events-none",
										)}
									/>
								)}
								<input
									className={cn(
										inputVariants({ variant, textSize }),
										(suffix ?? errorMessage) ? "pr-9" : "pr-3",
										Icon && iconPosition === "left" && "pl-9",
										Icon && iconPosition === "right" && "pr-9",
										truncate && "truncate",
										readOnly &&
											"cursor-default !text-opacity-70 ring-gray-900/15 focus:!outline-none focus:ring-gray-900/15 dark:!text-opacity-70 dark:ring-white/10 dark:focus:!outline-none dark:focus:ring-white/20",
										!errorMessage &&
											!readOnly &&
											"ring-gray-900/15 hover:ring-gray-900/15 focus:ring-green-600/80 dark:ring-white/10 dark:hover:ring-white/20 dark:focus:ring-green-600/80",
										Boolean(errorMessage) &&
											"!ring-red-300 focus:!ring-red-600/80 dark:!ring-red-500/50 dark:focus:!ring-red-500",
										inputClassName,
									)}
									ref={ref}
									name={name}
									id={name}
									readOnly={readOnly}
									aria-invalid={!!errorMessage}
									aria-describedby={`${name}-error`}
									onFocus={() => setIsFocus(true)}
									onBlur={handleBlur}
									autoComplete="one-time-code" // to remove autocomplete default behaviour
									type={type}
									{...(!allowPasswordManager && {
										"data-1p-ignore": "true",
										"data-lpignore": "true",
										"data-protonpass-ignore": "true",
									})}
									{...props}
								/>

								{isFocus && filteredSuggestions.length > 0 && !customList && (
									<div
										ref={suggestionListRef}
										className="border-primary absolute top-full z-50 mt-1 max-h-40 w-full overflow-y-auto rounded-md border bg-gray-50 text-primary shadow-lg backdrop-blur-md dark:bg-gray-850/80"
										onMouseEnter={() => setIsHovered(true)}
										onMouseLeave={() => setIsHovered(false)}
									>
										{filteredSuggestions.map((suggestion, index) => (
											<div
												key={`${suggestion}-${index}`}
												className="border-secondary border-b px-4 py-1.5 text-sm backdrop-blur-xl last:border-b-0 hover:bg-gray-100 dark:hover:bg-gray-800"
												onClick={() => handleSuggestionClick(suggestion)}
											>
												{suggestion}
											</div>
										))}
									</div>
								)}

								{isFocus && customList && customList}

								{!!postfix && (
									<span className="absolute right-0 top-1/2 -translate-y-1/2 text-tertiary">
										{postfix}
									</span>
								)}

								{(suffix ?? errorMessage) && (
									<div
										className={cn(
											"pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2",
											errorMessage ? "text-red-500" : "text-secondary",
										)}
									>
										{errorMessage && showErrorMessage ? (
											<AlertCircleIcon className="h-4 w-4" />
										) : (
											suffix
										)}
									</div>
								)}
							</div>
						</div>
					</div>
					{hint && (
						<div className={cn("mt-1.5 text-left text-sm text-primary")}>
							{hint}
						</div>
					)}
					<AnimatePresence>
						{errorMessage && showErrorMessage && (
							<motion.p
								initial={{
									opacity: 0,
									height: 0,
									marginTop: 0,
								}}
								animate={{
									opacity: 1,
									height: "auto",
									marginTop: "0.5rem",
								}}
								exit={{
									opacity: 0,
									height: 0,
									marginTop: 0,
								}}
								transition={{ duration: 0.1 }}
								className="mt-1.5 text-start text-sm text-red-600"
								id={`${name}-error`}
							>
								{errorMessage}
							</motion.p>
						)}
					</AnimatePresence>
				</div>
			)
		},
	),
)

type LucideIcon = FC<SVGProps<SVGSVGElement>>
